import { useEffect, useRef } from "react";
import useGA from "./use-ga";

/**
 * Fires legacy "Modal - Opened" / "Modal - Closed" GA events when `visible`
 * transitions across the open/close edge.
 *
 * Uses a ref-tracked previous-visibility flag rather than re-firing on every
 * render, so a parent re-render that doesn't change `visible` won't
 * double-count.
 *
 * Mirrors the legacy `app/js/controllers/mainctrl.js` modal lifecycle pattern
 * and the platform-side `platform/components/modal/index.js` events so any
 * existing Mixpanel cohort built on those category names continues to fire.
 */
const useModalAnalytics = (visible: boolean, label: string) => {
  const googleAnalytics = useGA();
  const wasVisibleRef = useRef(false);

  useEffect(() => {
    if (visible && !wasVisibleRef.current) {
      googleAnalytics.event({
        category: "Modal - Opened",
        action: "Select",
        label,
      });
      wasVisibleRef.current = true;
    } else if (!visible && wasVisibleRef.current) {
      googleAnalytics.event({
        category: "Modal - Closed",
        action: "Select",
        label,
      });
      wasVisibleRef.current = false;
    }
    // googleAnalytics is intentionally excluded from deps — the hook returns a
    // fresh object each render, and including it would re-fire the effect on
    // every parent render. The wasVisibleRef guard is the source of correctness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, label]);
};

export default useModalAnalytics;
